import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Spinner } from "./TrailBundle";
import {
  ModalBackdrop,
  ModalAccent,
  ModalBody,
  ModalCancelBtn,
  ModalCard,
  ModalCloseBtn,
  ModalCreateBtn,
  ModalFooter,
  ModalHeader,
  ModalHeaderContent,
  ModalSubtitle,
  ModalTitle,
  EmptyState,
  EmptyText,
  ClearSearchBtn,
  FieldError,
  UploadFilterBar,
  UploadSearchWrap,
  UploadSearchIcon,
  UploadSearchInput,
  UploadSummaryPill,
  UploadSourceRow,
  UploadSourceChip,
  UploadDocInfo,
  UploadDocName,
  UploadDocMeta,
  UploadMetaDot,
  UploadPagesBadge,
  UploadSourceTag,
  ChipDotSpan,
  UploadDocListBox,
  UploadDocItem,
  UploadDocRow,
  UploadCheckBox,
  UploadPartialDash,
  UploadPickedBadge,
  UploadExpandToggle,
  UploadPageInputWrap,
  UploadPageInputRow,
  UploadPageRangeField,
  UploadApplyBtn,
  UploadQuickBtn,
  UploadClearBtn,
  RangeErrorText,
  PickedLabelText,
  G,
  IcoChevronDown,
  IcoFile,
  IcoSearch,
  IcoX,
  Ico,
} from "./styles";
import { sourceChip } from "./CoreBundle";
import useDocuments from "../../../../hooks/useDocuments";

const SINGLE_PAGE_EXTENSIONS = new Set(["xlsx", "xls"]);

const getExtFromName = (name) => {
  if (!name) return "";
  const clean = String(name).split("?")[0].split("#")[0];
  const parts = clean.split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
};

const isSinglePageDoc = (doc) => {
  const ext = (doc?.ext || getExtFromName(doc?.name) || "").toLowerCase();
  return SINGLE_PAGE_EXTENSIONS.has(ext);
};
const getPageCount = (doc) => (isSinglePageDoc(doc) ? 1 : doc?.pages || 0);

export const buildPageRangeSchema = (maxPages) =>
  Yup.string()
    .required("Please enter a page range or a single page number")
    .test(
      "valid-range",
      "Must be valid pages/ranges like 1,3-5,7 or 1-10",
      (val) => {
        if (!val) return false;
        const trimmed = val.trim();
        if (!trimmed) return false;
        const segments = trimmed.split(",").map((s) => s.trim());
        return segments.every((seg) => {
          if (!seg) return false;
          const single = /^\d+$/.test(seg);
          const range = /^\d+\s*-\s*\d+$/.test(seg);
          return single || range;
        });
      },
    )
    .test(
      "range-order",
      "Start page must be less than or equal to end page",
      (val) => {
        if (!val) return true;
        const segments = val
          .trim()
          .split(",")
          .map((s) => s.trim());
        return segments.every((seg) => {
          const match = seg.match(/^(\d+)\s*-\s*(\d+)$/);
          if (!match) return true;
          return parseInt(match[1], 10) <= parseInt(match[2], 10);
        });
      },
    )
    .test(
      "within-bounds",
      `Page numbers must be between 1 and ${maxPages}`,
      (val) => {
        if (!val || !maxPages) return true;
        const segments = val
          .trim()
          .split(",")
          .map((s) => s.trim());
        return segments.every((seg) => {
          const single = seg.match(/^(\d+)$/);
          const range = seg.match(/^(\d+)\s*-\s*(\d+)$/);
          if (single) {
            const n = parseInt(single[1], 10);
            return n >= 1 && n <= maxPages;
          }
          if (range) {
            const from = parseInt(range[1], 10);
            const to = parseInt(range[2], 10);
            return from >= 1 && to <= maxPages;
          }
          return false;
        });
      },
    );

const parsePageRange = (rangeStr, maxPages) => {
  const trimmed = (rangeStr || "").trim();
  if (!trimmed) return new Set();

  const pages = new Set();
  const segments = trimmed.split(",").map((s) => s.trim());

  for (const seg of segments) {
    if (!seg) continue;
    const single = seg.match(/^(\d+)$/);
    const range = seg.match(/^(\d+)\s*-\s*(\d+)$/);

    if (single) {
      const n = parseInt(single[1], 10);
      if (n >= 1 && n <= maxPages) pages.add(n);
    } else if (range) {
      let from = parseInt(range[1], 10);
      let to = parseInt(range[2], 10);
      if (from > to) [from, to] = [to, from];
      from = Math.max(1, from);
      to = Math.min(maxPages, to);
      for (let i = from; i <= to; i++) pages.add(i);
    }
  }
  return pages;
};

const formatPagesAsRangeStr = (pageSet) => {
  const pages = Array.from(pageSet).sort((a, b) => a - b);
  if (pages.length === 0) return "";
  const parts = [];
  let start = pages[0];
  let prev = pages[0];
  for (let i = 1; i < pages.length; i++) {
    const cur = pages[i];
    if (cur === prev + 1) {
      prev = cur;
      continue;
    }
    parts.push(start === prev ? `${start}` : `${start}-${prev}`);
    start = cur;
    prev = cur;
  }
  parts.push(start === prev ? `${start}` : `${start}-${prev}`);
  return parts.join(",");
};

const PageRangeInput = ({ doc, pickedPages, setPickedPages }) => {
  const maxPages = getPageCount(doc);
  const [rangeStr, setRangeStr] = useState("");
  const [error, setError] = useState("");
  const [touched, setTouched] = useState(false);

  const picked = pickedPages[doc.id] || new Set();

  const validate = (val) => {
    const schema = buildPageRangeSchema(maxPages);
    try {
      schema.validateSync(val);
      return "";
    } catch (e) {
      return e.message;
    }
  };

  const handleChange = (e) => {
    if (!e?.target) return;
    const val = e.target.value;
    setRangeStr(val);
    if (touched) setError(validate(val));
  };

  const handleApply = () => {
    setTouched(true);
    const err = validate(rangeStr);
    if (err) {
      setError(err);
      return;
    }
    const pages = parsePageRange(rangeStr, maxPages);
    setPickedPages((p) => ({ ...p, [doc.id]: pages }));
    setRangeStr("");
    setError("");
    setTouched(false);
  };

  const handleClear = () => {
    setPickedPages((p) => ({ ...p, [doc.id]: new Set() }));
    setRangeStr("");
    setError("");
    setTouched(false);
  };

  const handleSelectAll = () => {
    if (!maxPages) return;
    const all = new Set(Array.from({ length: maxPages }, (_, i) => i + 1));
    setPickedPages((p) => ({ ...p, [doc.id]: all }));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleApply();
    }
  };

  return (
    <UploadPageInputWrap>
      <UploadPageInputRow>
        <UploadPageRangeField
          $error={!!error}
          placeholder={
            maxPages ? `e.g. 1-${maxPages} or 3,4` : "e.g. 1-5 or 3,4"
          }
          value={rangeStr}
          onChange={handleChange}
          onBlur={() => setTouched(true)}
          onKeyDown={handleKeyDown}
        />
        <UploadApplyBtn type="button" onClick={handleApply}>
          Add Pages
        </UploadApplyBtn>
        <UploadQuickBtn type="button" onClick={handleSelectAll}>
          All
        </UploadQuickBtn>
        {picked.size > 0 && (
          <UploadClearBtn type="button" onClick={handleClear}>
            Clear
          </UploadClearBtn>
        )}
      </UploadPageInputRow>

      {error && <RangeErrorText>{error}</RangeErrorText>}

      {picked.size > 0 && (
        <PickedLabelText>
          {picked.size} page{picked.size !== 1 ? "s" : ""} selected:{" "}
          {formatPagesAsRangeStr(picked)}
        </PickedLabelText>
      )}
    </UploadPageInputWrap>
  );
};

const UploadDocumentsModal = ({
  open,
  onClose,
  caseId,
  bundle,
  subFolder,
  onUploaded,
}) => {
  const [sourceFilter, setSourceFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [expandedDocs, setExpandedDocs] = useState({});
  const [pickedPages, setPickedPages] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const {
    documents,
    loading,
    error: loadError,
  } = useDocuments(caseId, sourceFilter);

  useEffect(() => {
    if (!open) return;
    setSourceFilter("All");
    setSearch("");
    setExpandedDocs({});
    setPickedPages({});
    setSubmitError("");
    setSubmitting(false);
  }, [open]);

  if (!open) return null;

  const filteredDocs = documents.filter((d) => {
    const okSource =
      sourceFilter === "All" ||
      d.source?.toLowerCase() === sourceFilter?.toLowerCase();
    const okSearch =
      !search || d.name?.toLowerCase().includes(search.toLowerCase());
    return okSource && okSearch;
  });

  const pickedDocIds = Object.keys(pickedPages).filter(
    (id) => pickedPages[id]?.size > 0,
  );
  const totalPickedPages = Object.values(pickedPages).reduce(
    (s, set) => s + (set?.size || 0),
    0,
  );

  const BUNDLE_PAGE_LIMIT = 2000;
  const alreadyUsed = (bundle?.bundles || []).reduce((sum, b) => {
    return (
      sum +
      (b.assignments || []).reduce((s, a) => {
        if (!a.pages) return s;
        const pages = parsePageRange(a.pages, Infinity);
        return s + pages.size;
      }, 0)
    );
  }, 0);
  const remaining = BUNDLE_PAGE_LIMIT - alreadyUsed;

  const handleSelectAllPages = (doc) => {
    const count = getPageCount(doc);
    if (!count) return;
    const all = new Set(Array.from({ length: count }, (_, i) => i + 1));
    setPickedPages((p) => ({ ...p, [doc.id]: all }));
  };

  const handleSubmit = async () => {
    if (pickedDocIds.length === 0) {
      setSubmitError("Pick at least one page from a document.");
      return;
    }
    if (totalPickedPages > remaining) {
      setSubmitError(
        `This bundle already has ${alreadyUsed} page${alreadyUsed !== 1 ? "s" : ""}. ` +
          `You can only add ${remaining} more page${remaining !== 1 ? "s" : ""} ` +
          `(limit is ${BUNDLE_PAGE_LIMIT}). You selected ${totalPickedPages}.`,
      );
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      const allAssignments =
        bundle?.bundles?.flatMap((b) => b.assignments || []) || [];

      let nextTabNum =
        allAssignments.length > 0
          ? Math.max(...allAssignments.map((a) => Number(a.tabNum || 0))) + 1
          : 1;
      const assignments = pickedDocIds.map((docId) => {
        const doc = documents.find((d) => String(d.id) === String(docId));
        return {
          sourceDocId: docId,
          userLabel: doc?.name || "Untitled Document",
          pages: formatPagesAsRangeStr(pickedPages[docId]),
          tabNum: nextTabNum++,
          fileUrl: doc?.fileUrl,
          visibilityType: doc?.visibilityType || "me",
        };
      });

      const payload = {
        bundleId: bundle?.id,
        sfCode: subFolder?.code,
        caseId: caseId,
        bundleCode: bundle?.bundleCode,
        totalPages: totalPickedPages,
        visibilityType: "me",
        assignments: assignments,
      };

      await onUploaded({
        bundleId: bundle.id,
        sfCode: subFolder.code,
        subFolderName: subFolder.name,
        isMainFolder: subFolder?.isMainFolder,
        items: payload,
      });

      onClose();
    } catch (err) {
      setSubmitError(err?.message || "Failed to upload files to folder.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalBackdrop onClick={onClose}>
      <ModalCard
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 640, width: "min(640px, 100%)" }}
      >
        <ModalHeader>
          <ModalAccent style={{ background: G.teal }} />
          <ModalHeaderContent>
            <ModalTitle>Upload Files to Folder</ModalTitle>
            <ModalSubtitle>
              Pick pages from existing case documents to add into{" "}
              {subFolder?.code === "__flat__" ? (
                bundle?.name
              ) : (
                <>
                  <strong>
                    {subFolder?.code} — {subFolder?.name}
                  </strong>{" "}
                  ({bundle?.bundleCode || bundle?.id} — {bundle?.name}).
                </>
              )}
            </ModalSubtitle>
          </ModalHeaderContent>
          <ModalCloseBtn onClick={onClose}>
            <IcoX size={14} color={G.slate500} />
          </ModalCloseBtn>
        </ModalHeader>

        <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
          <UploadFilterBar>
            <UploadSearchWrap>
              <UploadSearchIcon>
                <IcoSearch size={14} color={G.slate400} />
              </UploadSearchIcon>
              <UploadSearchInput
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search case documents…"
              />
            </UploadSearchWrap>
            <UploadSummaryPill>
              {totalPickedPages} / {remaining} page{remaining !== 1 ? "s" : ""}{" "}
              left · {pickedDocIds.length} doc
              {pickedDocIds.length !== 1 ? "s" : ""}
            </UploadSummaryPill>
            {totalPickedPages > 0 && (
              <ClearSearchBtn as="button" onClick={() => setPickedPages({})}>
                Clear all
              </ClearSearchBtn>
            )}
          </UploadFilterBar>

          <UploadSourceRow>
            {["All", "Claimant", "Respondent", "Neutral"].map((g) => {
              const chip = g !== "All" ? sourceChip(g) : null;
              return (
                <UploadSourceChip
                  key={g}
                  type="button"
                  $on={sourceFilter === g}
                  $bg={chip?.bg || G.slate100}
                  $fg={chip?.fg || G.slate700}
                  $dot={chip?.dot}
                  onClick={() => setSourceFilter(g)}
                >
                  {chip && <ChipDotSpan style={{ background: chip.dot }} />}
                  {g}
                </UploadSourceChip>
              );
            })}
          </UploadSourceRow>

          <UploadDocListBox>
            {loading ? (
              <EmptyState>
                <Spinner $size="24px" />
                <EmptyText style={{ marginTop: 10 }}>
                  Loading case documents…
                </EmptyText>
              </EmptyState>
            ) : loadError ? (
              <EmptyState>
                <EmptyText style={{ color: G.red }}>{loadError}</EmptyText>
              </EmptyState>
            ) : filteredDocs.length === 0 ? (
              <EmptyState>
                <EmptyText>No documents match your filter.</EmptyText>
              </EmptyState>
            ) : (
              filteredDocs.map((d) => {
                const chip = sourceChip(d.source);
                const picked = pickedPages[d.id] || new Set();
                const isExpanded = !!expandedDocs[d.id];
                const pageCount = getPageCount(d);

                return (
                  <UploadDocItem key={d.id}>
                    <UploadDocRow
                      onClick={() =>
                        setExpandedDocs((e) => ({ ...e, [d.id]: !e[d.id] }))
                      }
                    >
                      <UploadCheckBox
                        type="button"
                        $on={picked.size === pageCount && pageCount > 0}
                        $partial={picked.size > 0 && picked.size < pageCount}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (picked.size > 0) {
                            setPickedPages((p) => ({
                              ...p,
                              [d.id]: new Set(),
                            }));
                          } else {
                            handleSelectAllPages(d);
                          }
                        }}
                        aria-label={`Select all pages of ${d.name}`}
                      >
                        {picked.size === pageCount && pageCount > 0 ? (
                          <Ico size={10} color="#fff" sw="3">
                            <polyline points="20 6 9 17 4 12" />
                          </Ico>
                        ) : picked.size > 0 ? (
                          <UploadPartialDash />
                        ) : null}
                      </UploadCheckBox>

                      <IcoFile size={14} color={G.slate400} />

                      <UploadDocInfo>
                        <UploadDocName title={d.name}>{d.name}</UploadDocName>
                        <UploadDocMeta>
                          <span>{d.category}</span>
                          <UploadMetaDot />
                          <UploadPagesBadge>
                            {isSinglePageDoc(d)
                              ? "1 page"
                              : `${pageCount} page${pageCount !== 1 ? "s" : ""}`}
                          </UploadPagesBadge>
                          <UploadMetaDot />
                          <span>{d.date}</span>
                        </UploadDocMeta>
                      </UploadDocInfo>

                      <UploadSourceTag
                        $bg={chip.bg}
                        $fg={chip.fg}
                        $border={chip.border}
                      >
                        <ChipDotSpan style={{ background: chip.dot }} />
                        {d.source}
                      </UploadSourceTag>

                      {picked.size > 0 && (
                        <UploadPickedBadge>
                          {picked.size} picked
                        </UploadPickedBadge>
                      )}

                      <UploadExpandToggle $expanded={isExpanded}>
                        <IcoChevronDown size={12} color={G.navy} />
                      </UploadExpandToggle>
                    </UploadDocRow>

                    {isExpanded && (
                      <PageRangeInput
                        doc={{ ...d, pages: pageCount }}
                        pickedPages={pickedPages}
                        setPickedPages={setPickedPages}
                      />
                    )}
                  </UploadDocItem>
                );
              })
            )}
          </UploadDocListBox>

          {submitError && <FieldError>{submitError}</FieldError>}
        </ModalBody>

        <ModalFooter>
          <ModalCancelBtn onClick={onClose}>Cancel</ModalCancelBtn>
          <ModalCreateBtn
            onClick={handleSubmit}
            disabled={
              submitting ||
              pickedDocIds.length === 0 ||
              totalPickedPages > remaining
            }
            style={{
              background: G.teal,
              opacity:
                submitting ||
                pickedDocIds.length === 0 ||
                totalPickedPages > remaining
                  ? 0.6
                  : 1,
            }}
          >
            {submitting
              ? "Uploading…"
              : `Add ${totalPickedPages || ""} Page${totalPickedPages !== 1 ? "s" : ""}`}
          </ModalCreateBtn>
        </ModalFooter>
      </ModalCard>
    </ModalBackdrop>
  );
};

export default UploadDocumentsModal;
